<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BSidebar
      id="sbRsmReportCustomers"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Month"
              label-for="filterMonth"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <flat-pickr
                id="filterMonth"
                v-model="tableRecordings.filter.schedule"
                class="form-control"
                placeholder="--------- ----"
                :config="flatPickerConfig"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Officer"
              label-for="filterOfficer"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableRecordings.selected.fsOfficer"
                placeholder="filter officer here"
                input-id="filterOfficer"
                label="officer_name"
                clearable
                append-to-body
                :options="(tableRecordings.options.fsOfficers)"
                :loading="(tableRecordings.fetching.fsOfficers)"
                :disabled="(tableRecordings.busy || tableRecordings.fetching.fsOfficers)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterFSOfficers"
              >
                <template #option="{ officer_name, mobile_number, photo_url }">
                  <div class="d-flex flex-column flex-md-row py-1">
                    <div class="flex-grow-1 d-flex align-items-center">
                      <div class="mr-2">
                        <BAvatar
                          v-if="photo_url"
                          :src="getOfficerProfile(photo_url)"
                          variant="primary"
                          size="28px"
                        />
                        <BAvatar
                          v-else
                          :text="String(officer_name).charAt(0)"
                          variant="primary"
                          size="28px"
                        />
                      </div>
                      <div class="d-flex flex-column">
                        <div
                          class="text-break text-wrap"
                          v-text="officer_name"
                        />
                        <strong>{{ mobile_number || '' }}</strong>
                      </div>
                    </div>
                  </div>
                </template>
                <template #selected-option="{ officer_name, photo_url }">
                  <div class="d-flex flex-column flex-md-row">
                    <div class="flex-grow-1 d-flex align-items-center">
                      <div class="mr-2">
                        <BAvatar
                          v-if="photo_url"
                          :src="getOfficerProfile(photo_url)"
                          variant="primary"
                          size="28px"
                        />
                        <BAvatar
                          v-else
                          :text="String(officer_name).charAt(0)"
                          variant="primary"
                          size="28px"
                        />
                      </div>
                      <div
                        class="text-break text-wrap"
                        v-text="officer_name"
                      />
                    </div>
                  </div>
                </template>
                <template #no-options="">
                  no available officer
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="tableRecordings.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbRsmReportCustomers
                variant="primary"
              >
                <i
                  v-if="[settingFilters, tableRecordings.fetching.fsOfficers].some(isTrue => isTrue)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
      <BCol>
        <BOverlay
          rounded
          opacity="0.1"
          spinner-small
          class="d-inline"
          spinner-variant="warning"
          :show="exporter.excel.exporting"
          style="margin-bottom: 1.1px;"
        >
          <JsonExcel
            :class="[
              'btn btn-sm btn-primary', {
                'btn-outline-primary disabled': exporter.excel.exporting || !isValidForExport
              }
            ]"
            :fetch="excelProvider"
            :before-generate="onStartExcelExport"
            :before-finish="onFinishExcelExport"
            :name="excelFileName"
            :fields="exporter.excel.fields"
            :escape-csv="exporter.excel.escape"
            :type="exporter.excel.type"
            :header="exporter.excel.header"
            :footer="exporter.excel.footer"
            worksheet="Monthly Plans"
          >
            {{ exporter.excel.exporting ? 'Exporting' : 'Export As Excel' }}
          </JsonExcel>
        </BOverlay>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableRecordings"
      class="mt-3"
      :provider="tableProvider"
      :fields="tableRecordings.fields"
      :filter="tableRecordings.filter"
      :filter-on="tableRecordings.filterOn"
      :sort-by="tableRecordings.sortBy"
      :sort-desc="tableRecordings.sortDesc"
      :sort-direction="tableRecordings.sortDirection"
      :total-rows="tableRecordings.totalRows"
      :current-page="tableRecordings.currentPage"
      :per-page="tableRecordings.perPage"
      :stacked="tableRecordings.stacked"
      :busy="tableRecordings.busy"
    />
  </BContainer>
</template>
<script>
import QUERY, { isEmpty } from 'lodash'
import { mapGetters } from 'vuex'
import MOMENT from 'moment'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import { setTableOptions, getTableOptions } from '@/forage/asm/reports/monthly-plans'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MFSOfficers from '@/mixins/accounts/fsos'

import { SASMFsoMonthlyPlan } from '@/services/asm'

export default {

  name: 'ASMReportsMonthlyPlans',

  mixins: [
    MFSOfficers,
    MxMisc,
    MxFormatter,
    MxImageUrl
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        if (context.currentUser) {
          context.state.settings = true

          context.tableRecordings.selected.team = {
            id: context.currentUser?.team || ''
          }

          context.tableRecordings.selected.area = {
            id: context.currentUser?.area || ''
          }

          if (options) {
            const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

            context.tableRecordings = Object.assign(context.tableRecordings, {
              totalRows: totalRows || 0,
              sortBy: sortBy || 'id',
              sortDesc: sortDesc || false,
              perPage: perPage || 10,
              currentPage: currentPage || 0,
              filter: {
                search: filter?.search || '',
                schedule: filter?.schedule || '',
                officer: ''
              },
              editable: {
                fsOfficer: filter?.officer || ''
              }
            })
          } else {
            context.tableRecordings.currentPage = 1
          }

          context.$nextTick(() => {
            context.getFSOfficers(
              context.tableRecordings,
              '',
              context.currentUser.team,
              context.currentUser.area
            ).finally(() => {
              if (context.tableRecordings.editable.fsOfficer) {
                context.tableRecordings.selected.fsOfficer = QUERY.find(context.tableRecordings.options.fsOfficers, {
                  id: context.tableRecordings.editable.fsOfficer
                }) || ''

                context.tableRecordings.editable.fsOfficer = ''
              }

              context.state.settings = false
            })
          })
        }
      })
    })
  },

  middleware: [
    'auth',
    'asm'
  ],

  metaInfo: () => ({
    title: 'Report - Monthly Plans'
  }),

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false,
        settings: false
      },
      exporter: {
        excel: {
          exporting: false,
          header: [],
          escape: false,
          type: 'xls',
          fields: {
            Month: {
              field: '',
              callback: row => `="${row.schedule}"`
            },
            'Event Date': {
              field: '',
              callback: row => `="${row.event_date}"`
            },
            Area: {
              field: 'user.area.area_name'
            },
            Officer: {
              field: 'user',
              callback: user => {
                return `${user?.first_name} ${user?.last_name}`
              }
            },
            Activity: {
              field: 'activity.activity_name'
            },
            'Number of Activities': {
              field: 'number_of_activities'
            },
            'New Number of Activities': {
              field: 'new_number_of_activities'
            },
            Modified: {
              field: 'is_modified',
              callback: isModified => Number(isModified) ? 'Yes' : 'No'
            },
            Canceled: {
              field: 'deleted_at',
              callback: deletedAt => isEmpty(deletedAt) ? 'No' : 'Yes'
            }
          },
          footer: []
        }
      },
      tableRecordings: {
        busy: false,
        fetching: {
          fsOfficers: false
        },
        options: {
          fsOfficers: []
        },
        selected: {
          team: {
            id: ''
          },
          area: {
            id: ''
          },
          fsOfficer: ''
        },
        editable: {
          fsOfficer: ''
        },
        filter: {
          search: '',
          schedule: MOMENT().format('YYYY-MM'),
          officer: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 0,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          {
            mobile: 1,
            key: 'event_month',
            label: 'Month',
            class: 'min-w-75px',
            formatter: (_val, _key, row) => {
              return this.monthLongFormatter(`${row.schedule}-01`)
            }
          },
          { mobile: 2, key: 'event_date', class: 'min-w-75px', formatter: this.dateFormatter },
          { mobile: 3, key: 'user.area.area_name', label: 'Area', class: 'min-w-250px' },
          { mobile: 5, key: 'user', label: 'Officer', class: 'min-w-md-250px', formatter: creator => this.getFullName(null, null, creator), sortable: true },
          { mobile: 6, key: 'activity.activity_name', label: 'Activity', class: 'min-w-md-250px' },
          { mobile: 7, key: 'number_of_activities', class: 'text-center' },
          { mobile: 8, key: 'new_number_of_activities', class: 'text-center' },
          { mobile: 9, key: 'is_modified', label: 'Modified', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 10, key: 'deleted_at', label: 'Canceled', class: 'text-center', formatter: row => isEmpty(row) ? 'No' : 'Yes' }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    excelFileName () {
      const schedule = MOMENT(this.tableRecordings.filter.schedule).format('YYYY-MM')

      return `Monthly Plans ${schedule}`
    },

    isValidForExport () {
      const isDateFromNotEmpty = ![null, '', ' '].includes(this.tableRecordings.filter.schedule)

      return isDateFromNotEmpty && Number(this.tableRecordings.totalRows) >= 1
    },

    settingFilters () {
      const hasEditables = Object.keys(this.tableRecordings.editable).map(
        editableKey => this.tableRecordings.editable[editableKey]
      ).some(val => String(val) !== '')

      return hasEditables
    }
  },

  watch: {
    'tableRecordings.selected.fsOfficer' (fsOfficer) {
      this.tableRecordings.filter.officer = fsOfficer?.id || ''
    }
  },

  methods: {

    customerStatus (status) {
      switch (status) {
        case 'Verified': {
          return 'secondary'
        }
        case 'New': {
          return 'primary'
        }
        default: {
          return 'warning'
        }
      }
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      if (this.settingFilters) {
        return []
      }

      this.tableRecordings.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        creator_id: filter.officer,
        area_id: this.currentUser.area,
        schedule: filter.schedule
      }

      return SASMFsoMonthlyPlan.reports(payload).then(
        ({ data }) => {
          this.tableRecordings.totalRows = data.total_rows
          setTableOptions({
            currentPage,
            perPage,
            sortBy,
            sortDesc,
            filter,
            totalRows: data.total_rows
          })

          return data.items
        }
      ).catch(() => {
        this.tableRecordings.totalRows = 0
        setTableOptions({
          currentPage,
          perPage,
          sortBy,
          sortDesc,
          filter,
          totalRows: 0
        })

        return []
      }).finally(() => {
        this.tableRecordings.busy = false
      })
    },

    async searchFilterFSOfficers (search, loading) {
      this.searchFSOfficersProvider(this.tableRecordings, search, loading)
    },

    async excelProvider () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.busy = this.exporter.excel.exporting = true

      const payload = {
        filter_text: this.tableRecordings.filter.search,
        creator_id: this.tableRecordings.filter.officer,
        area_id: this.currentUser.area,
        schedule: this.tableRecordings.filter.schedule
      }

      return await SASMFsoMonthlyPlan.exports(payload).then(
        ({ data: { items } }) => {
          if (items.length <= 0) {
            this.swalInvalid(
              'No available data to be Exported'
            )
          }

          return items
        }
      ).catch(() => {
        return []
      }).finally(() => {
        this.exporter.busy = this.exporter.excel.exporting = false
      })
    },

    onStartExcelExport () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.excel.header = []
      this.exporter.excel.footer = []
    },

    onFinishExcelExport () {
    }
  }
}
</script>
